import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClientDto, UserDto } from '@cpm/shared';
import { useState } from 'react';
import { api } from '../api/client.js';
import { Modal } from './Modal.js';
import { Badge, Button, Card, CardHeader, ErrorNote, Input, Label } from './ui.js';

export function ClientLoginManagement({ client }: { client: ClientDto }) {
  const queryClient = useQueryClient();
  const users = useQuery({
    queryKey: ['users', 'client-logins'],
    queryFn: () => api.get<UserDto[]>('/api/auth/users'),
  });
  const logins = (users.data ?? []).filter((user) => user.role === 'client' && user.clientId === client.id);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [accountAdmin, setAccountAdmin] = useState(true);
  const [temporaryUser, setTemporaryUser] = useState<UserDto | null>(null);
  const [password, setPassword] = useState('');
  const [notice, setNotice] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const invite = useMutation({
    mutationFn: () => api.post(`/api/clients/${client.id}/portal-users/invite`, { name, email, accountAdmin }),
    onSuccess: async () => {
      await refresh();
      setInviteOpen(false);
      setName(''); setEmail('');
      setNotice('Invitation sent. The user can set their own password from the email link.');
    },
  });
  const sendLink = useMutation({
    mutationFn: (userId: string) => api.post(`/api/auth/users/${userId}/send-reset-link`, {}),
    onSuccess: () => setNotice('Password reset link sent to the login email.'),
  });
  const temporary = useMutation({
    mutationFn: () => api.post(`/api/auth/users/${temporaryUser!.id}/temporary-password`, { password }),
    onSuccess: async () => {
      await refresh();
      setTemporaryUser(null); setPassword('');
      setNotice('Temporary password set. Share it privately; the user must change it at sign-in.');
    },
  });
  const resetTwoFactor = useMutation({
    mutationFn: (userId: string) => api.post(`/api/auth/users/${userId}/reset-2fa`, {}),
    onSuccess: async () => { await refresh(); setNotice('Two-factor authentication reset.'); },
  });

  return (
    <>
      <Card className="mt-4">
        <CardHeader title="Portal logins" description="Client contact details are separate from login accounts."
          actions={<Button onClick={() => setInviteOpen(true)} disabled={client.status !== 'active'}>Invite portal user</Button>} />
        {notice && <p className="px-5 py-3 text-sm text-green-700 dark:text-green-300">{notice}</p>}
        {(users.error || sendLink.error || resetTwoFactor.error) &&
          <div className="px-5 py-2"><ErrorNote error={users.error ?? sendLink.error ?? resetTwoFactor.error} /></div>}
        {logins.length === 0 ? (
          <p className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">
            No portal login exists for this client. Invite a user before the customer can sign in.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {logins.map((user) => (
              <li key={user.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div>
                  <p className="font-medium">{user.name} <Badge tone={user.isActive ? 'green' : 'red'}>{user.isActive ? 'active' : 'disabled'}</Badge></p>
                  <p className="text-xs text-slate-500">{user.email} · {user.accountAdmin ? 'Account administrator' : 'Member'}</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button variant="ghost" disabled={!user.isActive || sendLink.isPending}
                    onClick={() => sendLink.mutate(user.id)}>Send reset link</Button>
                  <Button variant="ghost" onClick={() => { setTemporaryUser(user); setPassword(''); }}>Set temporary password</Button>
                  {user.twoFactorEnabled && <Button variant="ghost" onClick={() => {
                    if (window.confirm(`Reset two-factor authentication for ${user.email}? This signs them out everywhere.`)) resetTwoFactor.mutate(user.id);
                  }}>Reset 2FA</Button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Modal title="Invite portal user" open={inviteOpen} onClose={() => setInviteOpen(false)}>
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); invite.mutate(); }}>
          <div><Label htmlFor="invite-name">Name</Label><Input id="invite-name" required value={name} onChange={(event) => setName(event.target.value)} /></div>
          <div><Label htmlFor="invite-email">Login email</Label><Input id="invite-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={accountAdmin} onChange={(event) => setAccountAdmin(event.target.checked)} />Account administrator</label>
          <p className="text-sm text-slate-500">An email link lets this user set a password. A client can have several portal logins.</p>
          {invite.error && <ErrorNote error={invite.error} />}
          <Button type="submit" loading={invite.isPending}>Send invitation</Button>
        </form>
      </Modal>
      <Modal title={`Set temporary password for ${temporaryUser?.name ?? ''}`} open={temporaryUser !== null}
        onClose={() => { setTemporaryUser(null); setPassword(''); }}>
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); temporary.mutate(); }}>
          <div><Label htmlFor="temporary-password">Temporary password</Label>
            <Input id="temporary-password" type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(event) => setPassword(event.target.value)} />
          </div>
          <p className="text-sm text-slate-500">Share it privately. Existing sessions and reset links will be revoked, and the user must choose a new password at sign-in.</p>
          {temporary.error && <ErrorNote error={temporary.error} />}
          <Button type="submit" loading={temporary.isPending}>Set temporary password</Button>
        </form>
      </Modal>
    </>
  );
}
